import { useEffect, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface AncientEmpiresLayerProps {
    show: boolean;
    selectedYear: number;
}

/**
 * Component to display ancient empire boundaries
 * Shows territorial extents of major empires and kingdoms with temporal filtering
 */
export function AncientEmpiresLayer({ show, selectedYear }: AncientEmpiresLayerProps) {
    const map = useMap();
    const [empiresLayer, setEmpiresLayer] = useState<L.GeoJSON | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [allFeatures, setAllFeatures] = useState<unknown[]>([]);

    // Effect 1: Load the data only when show becomes true
    useEffect(() => {
        if (!show || allFeatures.length > 0 || isLoading) {
            return;
        }

        setIsLoading(true);

        // Fetch the GeoJSON file
        fetch('/ancient-empires.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                setAllFeatures(data.features || []);
                setIsLoaded(true);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error loading ancient empires:', error);
                setIsLoading(false);
            });
    }, [show, allFeatures.length, isLoading]);

    // Popup content generator
    const generatePopupContent = useCallback((feature: any) => {
        const { 
            name, 
            type,
            ruler,
            capital,
            lowerDate,
            upperDate,
            description,
            significance
        } = feature.properties;
        
        // Format year for display
        const formatYear = (year: number | null | undefined) => {
            if (year === null || year === undefined || year === 0) return 'Unknown';
            return year < 0 ? `${Math.abs(year)} BC` : `${year} AD`;
        };
        
        const dateRange = `${formatYear(lowerDate)} - ${formatYear(upperDate)}`;
        
        return `
            <div class="space-y-2 text-sm max-w-[280px]">
                <h3 class="font-semibold text-base">${name}</h3>
                ${type ? `<p><strong>Type:</strong> ${type}</p>` : ''}
                <p><strong>Period:</strong> ${dateRange}</p>
                ${capital ? `<p><strong>Capital:</strong> ${capital}</p>` : ''}
                ${ruler ? `<p><strong>Rulers:</strong> ${ruler}</p>` : ''}
                ${description ? `<p class="text-xs">${description}</p>` : ''}
                ${significance ? `
                    <div class="border-t border-gray-200 pt-2 mt-2">
                        <p class="font-semibold text-xs mb-1">Biblical Significance:</p>
                        <p class="text-xs">${significance}</p>
                    </div>
                ` : ''}
            </div>
        `;
    }, []);

    // Effect 2: Create layer once when data is loaded
    useEffect(() => {
        if (!show) {
            if (empiresLayer) {
                map.removeLayer(empiresLayer);
                setEmpiresLayer(null);
            }
            return;
        }
        
        if (allFeatures.length === 0 || empiresLayer) {
            return;
        }

        const geojsonData = {
            type: 'FeatureCollection',
            features: allFeatures
        };

        // Create a custom pane for empires to control z-index
        if (!map.getPane('empiresPane')) {
            const pane = map.createPane('empiresPane');
            pane.style.zIndex = '300'; // Below provinces (350) and roads (400)
        }

        // Empire color scheme
        const empireColors: Record<string, string> = {
            'Assyrian Empire': '#6B4C9A',        // Dark purple
            'Babylonian Empire': '#4169A0',      // Deep blue
            'Persian Empire': '#3A9B9B',         // Teal
            'Ptolemaic Kingdom': '#5B8B5B',      // Green
            'Seleucid Empire': '#9B9B5B',        // Yellow-green
            'Roman Empire': '#9B5B4C',           // Red-brown
            'Kingdom of Israel': '#7B8B9B',      // Blue-gray
            'Kingdom of Judah': '#8B7B6B',       // Brown-gray
            'Moab': '#9B8B7B',                   // Tan
            'Ammon': '#8B9B7B',                  // Olive
            'Edom': '#9B7B6B',                   // Reddish-brown
            'Aram-Damascus': '#7B9B8B',          // Sage
            'Philistia': '#8B7B9B',              // Purple-gray
            'default': '#8B8B8B'                 // Gray
        };

        // Create GeoJSON layer
        const layer = L.geoJSON(geojsonData as any, {
            pane: 'empiresPane',
            style: (feature) => {
                const empireName = feature?.properties?.name || '';
                const color = empireColors[empireName] || empireColors['default'];
                
                return {
                    color: color,
                    weight: 2,
                    opacity: 0.6,
                    fillColor: color,
                    fillOpacity: 0.12,  // Lower opacity for background context
                    className: 'cursor-pointer'
                };
            },
            onEachFeature: (feature, layer) => {
                // Add hover effects
                layer.on({
                    mouseover: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: 3,
                            fillOpacity: 0.25
                        });
                    },
                    mouseout: (e) => {
                        const l = e.target;
                        l.setStyle({
                            weight: 2,
                            fillOpacity: 0.12
                        });
                    }
                });
                
                // Add popup
                if (feature.properties) {
                    const popupContent = generatePopupContent(feature);
                    layer.bindPopup(popupContent, { maxWidth: 300 });
                }
            }
        });

        layer.addTo(map);
        setEmpiresLayer(layer);
    }, [map, show, allFeatures, empiresLayer, generatePopupContent]);

    // Effect 3: Update visibility when selectedYear changes
    useEffect(() => {
        if (!empiresLayer || !show) {
            return;
        }

        // Filter empires by timeline
        empiresLayer.eachLayer((layer: any) => {
            const feature = layer.feature;
            if (feature && feature.properties) {
                const lowerDate = feature.properties.lowerDate;
                const upperDate = feature.properties.upperDate;
                
                // Show if within date range
                const shouldShow = selectedYear >= lowerDate && selectedYear <= upperDate;
                
                if (shouldShow) {
                    if (!map.hasLayer(layer)) {
                        map.addLayer(layer);
                    }
                } else {
                    if (map.hasLayer(layer)) {
                        map.removeLayer(layer);
                    }
                }
            }
        });
    }, [selectedYear, empiresLayer, show, map]);

    return null;
}

